if (!customElements.get('p4m-apparel-brand-browser')) {
  class P4MApparelBrandBrowser extends HTMLElement {
    connectedCallback() {
      if (this.dataset.p4mReady === 'true') return;
      this.dataset.p4mReady = 'true';

      this.abortController = new AbortController();
      this.collectionRoot = this.closest('results-list') || document;
      this.grid = this.collectionRoot.querySelector('ul.product-grid');
      this.status = this.querySelector('[data-p4m-apparel-load-status]');
      this.handleClick = (event) => {
        const link = event.target.closest('[data-p4m-apparel-brand-link]');
        if (link && this.contains(link)) this.navigateToBrand(event, link);
      };
      this.addEventListener('click', this.handleClick);

      if (window.location.hash) this.activateHash(window.location.hash);

      if (this.grid && this.dataset.p4mApparelFragmentPath) {
        this.loadFullCatalog();
      } else {
        this.showFallbackStatus();
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      if (this.handleClick) this.removeEventListener('click', this.handleClick);
    }

    async loadFullCatalog() {
      this.setAttribute('aria-busy', 'true');

      try {
        const firstFragment = await this.fetchFragment(1);
        const totalPages = Math.max(
          1,
          Number.parseInt(firstFragment.dataset.pages || '1', 10)
        );
        const fragments = [firstFragment];

        for (let firstPage = 2; firstPage <= totalPages; firstPage += 3) {
          const pageBatch = [];
          for (
            let page = firstPage;
            page < firstPage + 3 && page <= totalPages;
            page += 1
          ) {
            pageBatch.push(this.fetchFragment(page));
          }
          fragments.push(...(await Promise.all(pageBatch)));
        }

        this.renderFullCatalog(fragments);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.warn('P4M apparel catalog kept its server-rendered fallback.', error);
          this.showFallbackStatus();
        }
      } finally {
        this.setAttribute('aria-busy', 'false');
      }
    }

    async fetchFragment(page) {
      const url = this.fragmentUrl(page);
      let latestError;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await fetch(url, {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal: this.abortController.signal
          });

          if (!response.ok) {
            throw new Error(`Apparel catalog request failed with ${response.status}.`);
          }

          const documentFragment = new DOMParser().parseFromString(
            await response.text(),
            'text/html'
          );
          const fragment = documentFragment.querySelector(
            '[data-p4m-apparel-all-fragment]'
          );

          if (!fragment) {
            throw new Error('The apparel catalog response was incomplete.');
          }

          return fragment;
        } catch (error) {
          latestError = error;
          if (error?.name === 'AbortError' || attempt === 1) throw error;
          await new Promise((resolve) => window.setTimeout(resolve, 450));
        }
      }

      throw latestError;
    }

    fragmentUrl(page) {
      const url = new URL(this.dataset.p4mApparelFragmentPath, window.location.origin);
      const currentUrl = new URL(window.location.href);

      currentUrl.searchParams.forEach((value, key) => {
        if (key === 'sort_by' || key.startsWith('filter.')) {
          url.searchParams.append(key, value);
        }
      });

      url.searchParams.set('view', 'p4m-apparel-all');
      url.searchParams.set('page', String(page));
      return url.toString();
    }

    renderFullCatalog(fragments) {
      const expectedTotal = Number.parseInt(fragments[0].dataset.total || '0', 10);
      const cardsByProduct = new Map();

      fragments.forEach((fragment) => {
        fragment
          .querySelectorAll('[data-p4m-apparel-fragment-card]')
          .forEach((card) => {
            const productId = card.dataset.productId;
            if (productId && !cardsByProduct.has(productId)) {
              cardsByProduct.set(productId, card);
            }
          });
      });

      if (
        cardsByProduct.size === 0 ||
        (expectedTotal > 0 && cardsByProduct.size !== expectedTotal)
      ) {
        throw new Error(
          `Expected ${expectedTotal} apparel products but received ${cardsByProduct.size}.`
        );
      }

      const groups = new Map();
      cardsByProduct.forEach((card) => {
        const key = card.dataset.p4mApparelVendorKey || 'other-brands';
        const label = card.dataset.p4mApparelVendorLabel || 'Other brands';
        if (!groups.has(key)) {
          groups.set(key, { cards: [], labels: new Map() });
        }

        const group = groups.get(key);
        group.cards.push(card);
        group.labels.set(label, (group.labels.get(label) || 0) + 1);
      });

      const sortedGroups = Array.from(groups, ([key, group]) => ({
        key,
        cards: group.cards,
        label: this.preferredLabel(group.labels)
      })).sort((left, right) =>
        left.label.localeCompare(right.label, undefined, {
          numeric: true,
          sensitivity: 'base'
        })
      );

      const gridContent = document.createDocumentFragment();
      sortedGroups.forEach((group) => {
        gridContent.append(this.brandHeading(group));
        group.cards.forEach((card) => gridContent.append(card));
      });

      this.grid.replaceChildren(gridContent);
      this.grid.dataset.lastPage = '1';
      this.grid.dataset.p4mApparelComplete = 'true';
      this.rebuildNavigation(sortedGroups);
      this.removePagination();

      if (this.status) {
        const noun = cardsByProduct.size === 1 ? 'product is' : 'products are';
        this.status.textContent = `All ${cardsByProduct.size} ${noun} organized by brand below.`;
      }

      if (window.location.hash) this.activateHash(window.location.hash);
    }

    preferredLabel(labels) {
      return Array.from(labels.entries())
        .sort(([leftLabel, leftCount], [rightLabel, rightCount]) => {
          const styleDifference =
            this.labelStyleScore(rightLabel) - this.labelStyleScore(leftLabel);
          if (styleDifference !== 0) return styleDifference;
          if (rightCount !== leftCount) return rightCount - leftCount;
          return leftLabel.localeCompare(rightLabel, undefined, {
            sensitivity: 'base'
          });
        })[0][0];
    }

    labelStyleScore(label) {
      const letters = label.replace(/[^a-z]/gi, '');
      const isMixedCase =
        letters && letters !== letters.toUpperCase() && letters !== letters.toLowerCase();
      return isMixedCase ? 2 : 1;
    }

    brandHeading(group) {
      const heading = document.createElement('li');
      heading.className = 'p4m-brand-section-heading p4m-apparel-brand-section';
      heading.id = this.brandTargetId(group.key);
      heading.dataset.p4mApparelBrandSection = '';

      const eyebrow = document.createElement('span');
      eyebrow.className = 'p4m-brand-section-heading__eyebrow';
      eyebrow.textContent = 'Brand';

      const title = document.createElement('span');
      title.className = 'p4m-brand-section-heading__title';
      title.textContent = group.label;

      const count = document.createElement('span');
      count.className = 'p4m-brand-section-heading__count';
      count.textContent = `${group.cards.length} ${
        group.cards.length === 1 ? 'product' : 'products'
      }`;

      heading.append(eyebrow, title, count);
      return heading;
    }

    rebuildNavigation(groups) {
      const navigation = this.querySelector('.p4m-apparel-brand-browser__list');
      if (!navigation) return;

      const links = document.createDocumentFragment();
      groups.forEach((group) => {
        const link = document.createElement('a');
        link.href = `#${this.brandTargetId(group.key)}`;
        link.dataset.p4mApparelBrandLink = '';

        const label = document.createElement('span');
        label.textContent = group.label;

        const count = document.createElement('b');
        count.textContent = String(group.cards.length);

        link.append(label, count);
        links.append(link);
      });

      navigation.replaceChildren(links);
    }

    brandTargetId(vendorKey) {
      return `p4m-apparel-brand-${vendorKey}-${
        this.dataset.p4mApparelSectionId || 'collection'
      }`;
    }

    removePagination() {
      this.collectionRoot
        .querySelectorAll(
          '.p4m-fitment-pagination, [ref="viewMoreNext"], [ref="viewMorePrevious"]'
        )
        .forEach((element) => element.remove());
    }

    showFallbackStatus() {
      if (!this.status) return;
      const visibleProducts = this.grid
        ? this.grid.querySelectorAll('[data-product-id]').length
        : 0;
      this.status.textContent = `Showing ${visibleProducts || 'the available'} products.`;
    }

    navigateToBrand(event, link) {
      const targetId = link.hash ? decodeURIComponent(link.hash.slice(1)) : '';
      const target = targetId ? document.getElementById(targetId) : null;
      if (!target) return;

      event.preventDefault();
      this.setActiveLink(link);

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const headerOffset = window.matchMedia('(max-width: 749px)').matches ? 88 : 108;
      const targetTop = target.getBoundingClientRect().top + window.scrollY - headerOffset;

      window.history.replaceState(null, '', `#${targetId}`);
      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }

    activateHash(hash) {
      const link = Array.from(
        this.querySelectorAll('[data-p4m-apparel-brand-link]')
      ).find((candidate) => candidate.hash === hash);
      if (link) this.setActiveLink(link);
    }

    setActiveLink(activeLink) {
      this.querySelectorAll('[data-p4m-apparel-brand-link]').forEach((link) => {
        const selected = link === activeLink;
        link.classList.toggle('is-active', selected);
        if (selected) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }
  }

  customElements.define('p4m-apparel-brand-browser', P4MApparelBrandBrowser);
}
